import { memo, useMemo, type ReactNode } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { makeStyles, tokens, mergeClasses, Tooltip } from '@coreai/fluentui-react';
import {
  BranchForkFilled,
  WarningRegular,
  CheckmarkCircleFilled,
  DismissCircleFilled,
} from '@fluentui/react-icons';
import { useHandleLayout } from './useHandlePositions';
import { useIsHorizontalLayout } from '@/hooks/useLayoutOrientation';
import { ExecutionOverlay } from '@/components/shared';
import {
  FloatingNode,
  NodeHoverToolbar,
  useDetailLevel,
  type NodeStatus,
} from './FloatingNode';
import { getBuiltInManifest } from '@/data/manifests';
import type { NodeExecutionStatus } from '@/types/monitoring';

export type FloatingIfNodeData = {
  label: string;
  actionType: string;
  actionName: string;
  /** Human-readable condition summary, e.g. "status = \"Approved\"" */
  conditionSummary?: string;
  // Monitoring mode props
  executionStatus?: NodeExecutionStatus;
  executionDuration?: number;
  // Debug mode props
  isDirty?: boolean;
  /** Which branch was taken during execution: 'true', 'false', or undefined */
  branchTaken?: 'true' | 'false';
  // Toolbar action callbacks (injected by WorkflowDesigner)
  onOpen?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onEditWithAI?: () => void;
};

export type FloatingIfNodeType = Node<FloatingIfNodeData, 'floatingIf'>;

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  // Container for the two branch outputs (vertical layout - below node)
  branchContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalXL,
    marginTop: tokens.spacingVerticalS,
    // Stagger: appears after icon + label
    animationName: {
      from: { opacity: 0, transform: 'translateY(-4px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMax,
    animationFillMode: 'backwards',
    animationDelay: tokens.durationFaster,
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
      animationDelay: '0ms',
    },
  },
  // Individual branch wrapper
  branchWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    position: 'relative',
  },
  // Branch label styling
  branchLabel: {
    fontSize: '11px', // No exact Fluent token for 11px — intentional half-step
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  trueLabel: {
    color: tokens.colorPaletteGreenForeground1,
  },
  falseLabel: {
    color: tokens.colorPaletteRedForeground1,
  },
  afterLabel: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightBold,
  },
  // Handle styling with expanded hit area for accessibility
  handleBase: {
    width: '12px !important',
    height: '12px !important',
    borderRadius: '50%',
    position: 'relative',
    transform: 'none',
    top: '0',
    left: '0',
    cursor: 'crosshair',
    transitionProperty: 'box-shadow',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    // Expand hit area to 44px for touch accessibility
    '::before': {
      content: '""',
      position: 'absolute',
      width: '44px',
      height: '44px',
      left: '-16px',
      top: '-16px',
      borderRadius: '50%',
    },
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        boxShadow: tokens.shadow4,
      },
    },
  },
  trueHandle: {
    backgroundColor: `${tokens.colorPaletteGreenBackground2} !important`,
    border: `2px solid ${tokens.colorPaletteGreenBorder2} !important`,
  },
  falseHandle: {
    backgroundColor: `${tokens.colorPaletteRedBackground2} !important`,
    border: `2px solid ${tokens.colorPaletteRedBorder2} !important`,
  },
  mergeHandle: {
    backgroundColor: `${tokens.colorNeutralBackground3} !important`,
    border: `2px solid ${tokens.colorNeutralStroke1} !important`,
  },
  // After (merge) handle in vertical mode is sized up relative to True/False
  // to reinforce that it is the *primary* downstream path. True/False are
  // diversions; After is the spine that always runs once the If completes.
  mergeHandleVertical: {
    width: '14px !important',
    height: '14px !important',
    // Nudge the handle's vertical center down 1px so the larger circle still
    // optically aligns with the True/False handle centers. The +2px diameter
    // would otherwise pull its centroid up relative to its siblings.
    top: '1px',
  },
  // Horizontal-mode merge handle: visible neutral dot at right corner of diamond
  mergeHandleHorizontal: {
    width: '10px !important',
    height: '10px !important',
    borderRadius: '50%',
    backgroundColor: `${tokens.colorNeutralBackground3} !important`,
    border: `2px solid ${tokens.colorNeutralStroke1} !important`,
    // Anchor the handle to the diamond's right corner. ReactFlow's <Handle>
    // is absolutely positioned; the className wins over default centering.
    top: '28px',
    transitionProperty: 'box-shadow',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        boxShadow: tokens.shadow4,
      },
    },
  },
  afterIcon: {
    color: tokens.colorNeutralForeground2,
  },
  // Horizontal mode handles - positioned at diamond corners (BPMN standard)
  // Diamond is 56px rotated 45°; top corner ≈ (28, 0), bottom corner ≈ (28, 56)
  // Edges route upward (true) and downward (false) then curve to downstream nodes
  handleCorner: {
    width: '10px !important',
    height: '10px !important',
    borderRadius: '50%',
    transitionProperty: 'box-shadow',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        boxShadow: tokens.shadow4,
      },
    },
  },
  // Branch taken indicator
  branchTaken: {
    fontWeight: tokens.fontWeightBold,
  },
  branchNotTaken: {
    opacity: 0.4,
  },
  // Branch icons for accessibility (not color-only differentiation)
  branchIcon: {
    fontSize: tokens.fontSizeBase100,
    flexShrink: 0,
  },
  trueIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  falseIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  // Dirty indicator - z-index below handles but above node
  dirtyIndicator: {
    position: 'absolute',
    top: '-4px',
    left: '-4px',
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
    borderRadius: '50%',
    width: '14px',
    height: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase100,
    zIndex: 5, // Below handles (z-index 10) but above node content
    boxShadow: tokens.shadow4,
  },
  // Position the False handle near the bottom corner of the diamond bbox.
  // `Position.Bottom` alone anchors to the wrapper (which extends past the
  // diamond shape due to the label below); top:44px on a 56-tall bbox keeps
  // the dot on the visible bottom corner. Phantom anchoring in
  // workflowHelpers.ts uses fraction 49/56 to match this dot's center.
  handleFalsePosition: {
    bottom: 'auto',
    top: '44px',
  },
});

/**
 * Internal helper that renders one vertical-mode branch output:
 * a wrapper, an icon + label span, and a source Handle.
 *
 * Extracted so the three siblings (True / After / False) don't repeat the
 * same JSX template inline. Variation per branch is expressed entirely via
 * props — this component does NOT know which branch it is. The `icon`
 * prop is expected to be a fully-styled icon node (caller applies the
 * icon className), keeping the rendered DOM identical to the previous
 * inline blocks.
 */
type BranchOutputProps = {
  label: string;
  icon: ReactNode;
  handleId: string;
  handleAriaLabel: string;
  labelClassName: string;
  handleClassName: string;
  isConnectable: boolean;
  wrapperClassName: string;
};

const BranchOutput = ({
  label,
  icon,
  handleId,
  handleAriaLabel,
  labelClassName,
  handleClassName,
  isConnectable,
  wrapperClassName,
}: BranchOutputProps) => (
  <div className={wrapperClassName}>
    <span className={labelClassName}>
      {icon}
      {label}
    </span>
    <Handle
      type="source"
      position={Position.Bottom}
      id={handleId}
      isConnectable={isConnectable}
      className={handleClassName}
      aria-label={handleAriaLabel}
    />
  </div>
);

/**
 * Compact floating-style If Node with true/false branch outputs.
 * Shows icon + label with two colored output handles below.
 */
export const FloatingIfNode = memo(
  ({ data, selected }: NodeProps<FloatingIfNodeType>) => {
    const styles = useStyles();
    const handles = useHandleLayout();
    const detailLevel = useDetailLevel();
    const isHorizontal = useIsHorizontalLayout();

    const builtInManifest = useMemo(() => getBuiltInManifest(data.actionType), [data.actionType]);
    const brandColor = builtInManifest?.properties?.brandColor as string | undefined;

    // Hover state is no longer needed — empty branch + buttons are always
    // visible and the NodeHoverToolbar manages its own hover state.

    // Branch taken state for monitoring mode
    const trueBranchTaken = data.branchTaken === 'true';
    const falseBranchTaken = data.branchTaken === 'false';
    const hasBranchInfo = data.branchTaken !== undefined;

    // Map execution status to FloatingNode status
    const getNodeStatus = (): NodeStatus => {
      if (!data.executionStatus) return 'default';

      switch (data.executionStatus) {
        case 'running':
          return 'running';
        case 'succeeded':
          return 'success';
        case 'failed':
          return 'failed';
        case 'skipped':
          return 'skipped';
        default:
          return 'default';
      }
    };

    const isMonitoringMode = data.executionStatus !== undefined;

    return (
      <div
        className={styles.wrapper}
      >        
        {/* Dirty indicator */}
        {data.isDirty && (
          <Tooltip
            content="Modified since execution"
            relationship="label"
          >
            <div className={styles.dirtyIndicator} aria-hidden="true">
              <WarningRegular />
            </div>
          </Tooltip>
        )}

        <NodeHoverToolbar
          onConfigure={data.onOpen}
          onDelete={data.onDelete}
          onDuplicate={data.onDuplicate}
          onEditWithAI={data.onEditWithAI}
          showToolbar={!isMonitoringMode}
          disabled={selected}
        >
          <FloatingNode
            icon={<BranchForkFilled aria-hidden="true" />}
            label={data.label}
            subLabel={data.conditionSummary ? `If · ${data.conditionSummary}` : 'If'}
            detailLevel={detailLevel}
            selected={selected}
            status={getNodeStatus()}
            accentColor="purple"
            brandColorOverride={brandColor}
            shape="diamond"
            ariaLabel={`${data.label} if condition node${hasBranchInfo ? ` (${data.branchTaken} branch taken)` : ''}`}
          >
            <Handle
              type="target"
              position={handles.input.position}
              id="input"
              isConnectable={true}
              style={handles.input.style}
            />
            {/* Horizontal mode: handles at diamond top/bottom corners */}
            {isHorizontal && (
              <>
                <Handle
                  type="source"
                  position={Position.Top}
                  id="true"
                  isConnectable={!isMonitoringMode}
                  className={mergeClasses(styles.handleCorner, styles.trueHandle)}
                  aria-label="True branch output"
                />
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id="false"
                  isConnectable={!isMonitoringMode}
                  className={mergeClasses(styles.handleCorner, styles.falseHandle, styles.handleFalsePosition)}
                  aria-label="False branch output"
                />
              </>
            )}
            {/* Merge handle — terminal/runAfter edge for downstream sequencing
                that should run after the entire If completes (regardless of branch).
                In horizontal mode it lives at the diamond's right corner; in
                vertical mode it appears alongside the true/false outputs below. */}
            {isHorizontal && (
              <Handle
                type="source"
                position={Position.Right}
                id="merge"
                isConnectable={!isMonitoringMode}
                className={styles.mergeHandleHorizontal}
                aria-label="After If (runAfter) output"
              />
            )}
          </FloatingNode>
        </NodeHoverToolbar>

        {/* Branch outputs - vertical layout (below node).
            Order is intentional: True | After | False. Putting the After
            (runAfter) handle in the center reinforces it as the *primary*
            downstream path — True/False are detours that both rejoin the
            main flow at After. Mirror symmetry around the centered spine
            matches BPMN gateway conventions and stops "After" from reading
            as just a third sibling branch.
            Empty True/False branches show their + affordance through the
            TerminalEdge (a colored dashed stub-edge ending in a + button),
            so the inline branch labels here are display-only. */}
        {!isHorizontal && (
          <div className={styles.branchContainer}>
            <BranchOutput
              label="True"
              icon={
                <CheckmarkCircleFilled
                  className={mergeClasses(styles.branchIcon, styles.trueIcon)}
                  aria-hidden="true"
                />
              }
              handleId="true"
              handleAriaLabel="True branch output"
              labelClassName={mergeClasses(
                styles.branchLabel,
                styles.trueLabel,
                hasBranchInfo && trueBranchTaken && styles.branchTaken,
                hasBranchInfo && !trueBranchTaken && styles.branchNotTaken,
              )}
              handleClassName={mergeClasses(styles.handleBase, styles.trueHandle)}
              isConnectable={!isMonitoringMode}
              wrapperClassName={styles.branchWrapper}
            />

            {/* After (runAfter) terminal output — runs after the entire If completes.
                Centered between True/False to read as the primary continuation. */}
            <BranchOutput
              label="After"
              icon={
                <BranchForkFilled
                  className={mergeClasses(styles.branchIcon, styles.afterIcon)}
                  aria-hidden="true"
                />
              }
              handleId="merge"
              handleAriaLabel="After If (runAfter) output"
              labelClassName={mergeClasses(styles.branchLabel, styles.afterLabel)}
              handleClassName={mergeClasses(
                styles.handleBase,
                styles.mergeHandle,
                styles.mergeHandleVertical,
              )}
              isConnectable={!isMonitoringMode}
              wrapperClassName={styles.branchWrapper}
            />

            <BranchOutput
              label="False"
              icon={
                <DismissCircleFilled
                  className={mergeClasses(styles.branchIcon, styles.falseIcon)}
                  aria-hidden="true"
                />
              }
              handleId="false"
              handleAriaLabel="False branch output"
              labelClassName={mergeClasses(
                styles.branchLabel,
                styles.falseLabel,
                hasBranchInfo && falseBranchTaken && styles.branchTaken,
                hasBranchInfo && !falseBranchTaken && styles.branchNotTaken,
              )}
              handleClassName={mergeClasses(styles.handleBase, styles.falseHandle)}
              isConnectable={!isMonitoringMode}
              wrapperClassName={styles.branchWrapper}
            />
          </div>
        )}

        {/* Execution overlay */}
        {isMonitoringMode && (
          <ExecutionOverlay
            status={data.executionStatus!}
            duration={data.executionDuration}
            position="top-right"
          />
        )}
      </div>
    );
  }
);

FloatingIfNode.displayName = 'FloatingIfNode';
